/**
 * Admin Protected Route Guard (PR A6 - Enhanced)
 * Redirects to login if not authenticated
 * Includes browser history navigation protection (back/forward)
 */
import React, { useEffect, useMemo } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getUserFromToken } from '../utils/adminApi';

/**
 * Validates JWT token exists and is not expired
 */
const validateToken = () => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      return false;
    }
    
    // Decode and check expiration
    const parts = token.split('.');
    if (parts.length !== 3) {
      return false;
    }
    
    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp;
    
    if (!exp) {
      return false;
    }
    
    // Check if expired (with 30 second buffer)
    const now = Math.floor(Date.now() / 1000);
    return exp > now + 30;
  } catch (error) {
    console.error('[AdminProtectedRoute] Token validation error:', error);
    return false;
  }
};

/**
 * Clears all auth data
 */
const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
};

/**
 * Check if user has admin or IT/jurisdiction manager role (allowed to access admin routes)
 *
 * NOTE: This is the FIRST gate on /admin/* — it lets the role reach the shell.
 * Individual admin pages (e.g. User Management, IT Dashboard tabs) still
 * enforce their own stricter per-route role checks, so adding a non-admin
 * role here doesn't silently expand access to everything under /admin/*.
 */
const checkAdminRole = (pathname = '') => {
  const user = getUserFromToken();
  if (!user) return false;
  
  // Admin roles have full access
  const adminRoles = ['admin'];
  // IT roles need access to the IT Portal (/admin/it-dashboard) and related
  // IT-operations pages. it_support was previously missing, which caused a
  // redirect loop for that persona (landing route → guard → /dashboard
  // → landing route again).
  const itRoles = ['it_manager', 'it_support'];
  // Jurisdiction Manager has access to jurisdiction routes
  // Jurisdiction Requestor (Session 24) can access /admin/jurisdictions (read-only) and /admin/jurisdiction-requests
  const jurisdictionRoles = ['jurisdiction_manager', 'jurisdiction_requestor'];
  // HR-PERSONA-001 (Jul 2026): HR roles reach /admin/hr/* and
  // /admin/recruitment/*; backend enforces read-only for hr_specialist.
  const hrRoles = ['hr_manager', 'hr_specialist', 'hr'];
  
  // Normalized: lowercase + spaces→underscores so stale sessions that stored
  // display-style roles ("IT Support") still gate correctly.
  const userRole = String(user.role || '').toLowerCase().trim().replace(/\s+/g, '_');

  // Accounting is PATH-SCOPED (Jul 2026): only Client Pricing and Product
  // Master render inside the AdminLayout shell for them. Several /admin
  // settings pages have no per-page role guard, so the shell gate must
  // not be broad for finance roles.
  if (userRole === 'accounting') {
    const accountingPaths = ['/admin/pricing', '/admin/products', '/admin/online-coverage', '/admin/ground-rates', '/admin/import-history'];
    return accountingPaths.some(p => pathname === p || pathname.startsWith(p + '/'));
  }

  return adminRoles.includes(userRole) || itRoles.includes(userRole) || jurisdictionRoles.includes(userRole) || hrRoles.includes(userRole);
};

const AdminProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Compute auth validity synchronously (not in effect)
  const authState = useMemo(() => {
    const tokenValid = validateToken();
    const isAdmin = checkAdminRole(location.pathname);
    
    console.log('[AdminProtectedRoute] Checking auth:', { 
      tokenValid, 
      isAdmin, 
      path: location.pathname 
    });
    
    if (!tokenValid) {
      return { valid: false, reason: 'token_invalid' };
    }
    
    if (!isAdmin) {
      return { valid: false, reason: 'not_admin' };
    }
    
    return { valid: true, reason: null };
  }, [location.pathname, location.key]); // Re-evaluate on route changes
  
  // Handle redirects in effect (side effect, not state)
  useEffect(() => {
    if (!authState.valid) {
      if (authState.reason === 'token_invalid') {
        console.log('[AdminProtectedRoute] Token invalid, redirecting to login');
        clearAuthData();
        // SECURITY FIX: Don't pass 'from' state - always redirect to dashboard after login
        navigate('/login', { replace: true });
      } else if (authState.reason === 'not_admin') {
        console.log('[AdminProtectedRoute] Not admin, redirecting to dashboard');
        navigate('/dashboard', { replace: true });
      }
    }
  }, [authState, navigate, location]);
  
  // Listen for browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      console.log('[AdminProtectedRoute] Browser navigation detected');
      
      if (!validateToken()) {
        console.log('[AdminProtectedRoute] Auth invalid on popstate');
        clearAuthData();
        // Feb 2026 unified logout notice — token-validation force-logout.
        try {
          const { queueLogoutNotice } = require('../utils/logoutNotice');
          queueLogoutNotice('session_invalid');
        } catch (_) { /* helper not loaded */ }
        window.location.replace('/login');
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  
  // Validate on tab focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!validateToken()) {
          console.log('[AdminProtectedRoute] Auth invalid on tab focus');
          clearAuthData();
          try {
            const { queueLogoutNotice } = require('../utils/logoutNotice');
            queueLogoutNotice('session_invalid');
          } catch (_) { /* helper not loaded */ }
          window.location.replace('/login');
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);
  
  // If not valid, render Navigate (will cause redirect)
  if (!authState.valid) {
    if (authState.reason === 'token_invalid') {
      // SECURITY FIX: Don't pass 'from' state - always redirect to dashboard after login
      return <Navigate to="/login" replace />;
    }
    if (authState.reason === 'not_admin') {
      return <Navigate to="/dashboard" replace />;
    }
  }
  
  return children;
};

export default AdminProtectedRoute;
